/**
 * Mises en forme de la vue d'aigle : euros, pourcentages, mois et dates, dans
 * la langue de l'interface. Tout montant arrive en centimes (`VueDAigle`) et
 * s'écrit en euros ; un montant `null` n'est jamais formaté ici, c'est le
 * composant `Montant` qui écrit « à saisir ».
 */
import { useTranslation } from 'react-i18next';
import { langueCourante } from '../../lib/modules.js';

/** Fuseau des mois et des jours de la plateforme (spec § 5). */
const FUSEAU = 'Europe/Paris';

export interface Formats {
  langue: string;
  /** Des centimes en euros : « 8 160 € », « 12,50 € ». */
  euros: (centimes: number) => string;
  /** Une valeur déjà en euros, en notation courte pour un axe : « 2,5 k ». */
  axeEuros: (euros: number) => string;
  /** Un ratio (0,782) en pourcentage : « 78,2 % ». */
  pourcent: (ratio: number) => string;
  /** Idem avec le signe : « +8,4 % », « −3 % », « 0 % ». */
  pourcentSigne: (ratio: number) => string;
  /** Des centimes en euros avec le signe : « +630 € ». */
  eurosSigne: (centimes: number) => string;
  /** Un entier avec le signe : « +1 », « 0 ». */
  nombreSigne: (valeur: number) => string;
  /** Des points de base (1 000) en pourcentage : « 10 % ». */
  pourcentDePoints: (points: number) => string;
  nombre: (valeur: number) => string;
  /** `AAAA-MM` → « sept. », pour un axe. */
  moisCourt: (mois: string) => string;
  /** `AAAA-MM` → « sept. 2026 », pour un tableau serré. */
  moisAnnee: (mois: string) => string;
  /** `AAAA-MM` → « septembre 2026 ». */
  moisLong: (mois: string) => string;
  /** Un instant ISO → « 17 sept. 2026 ». */
  date: (iso: string) => string;
  /** Un instant ISO → « 6 octobre », sans l'année. */
  jourMois: (iso: string) => string;
  /** Un instant ISO → « 1 octobre 2026 ». */
  jourMoisAnnee: (iso: string) => string;
  /** Un instant ISO → « 30 sept., 03:12 ». */
  dateHeure: (iso: string) => string;
}

function dateDuMois(mois: string): Date {
  const [annee, numero] = mois.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(annee, numero - 1, 1));
}

export function creerFormats(langue: string): Formats {
  // Un montant rond s'écrit sans centimes (« 390 € »), les autres avec (« 163,20 € »).
  const eurosEntiers = new Intl.NumberFormat(langue, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const eurosCentimes = new Intl.NumberFormat(langue, { style: 'currency', currency: 'EUR' });
  const eurosSigne = new Intl.NumberFormat(langue, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero',
  });
  const pourcentageSigne = new Intl.NumberFormat(langue, {
    style: 'percent',
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  });
  const entierSigne = new Intl.NumberFormat(langue, { signDisplay: 'exceptZero' });
  const decimale = new Intl.NumberFormat(langue, { maximumFractionDigits: 1 });
  const pourcentage = new Intl.NumberFormat(langue, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const entier = new Intl.NumberFormat(langue);
  const moisCourt = new Intl.DateTimeFormat(langue, { month: 'short', timeZone: 'UTC' });
  const moisAnnee = new Intl.DateTimeFormat(langue, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const moisLong = new Intl.DateTimeFormat(langue, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const date = new Intl.DateTimeFormat(langue, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: FUSEAU,
  });
  const jourMois = new Intl.DateTimeFormat(langue, {
    day: 'numeric',
    month: 'long',
    timeZone: FUSEAU,
  });
  const jourMoisAnnee = new Intl.DateTimeFormat(langue, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: FUSEAU,
  });
  const dateHeure = new Intl.DateTimeFormat(langue, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: FUSEAU,
  });

  return {
    langue,
    euros: (centimes) =>
      (centimes % 100 === 0 ? eurosEntiers : eurosCentimes).format(centimes / 100),
    axeEuros: (valeur) =>
      valeur >= 1000 ? `${decimale.format(valeur / 1000)} k` : decimale.format(valeur),
    pourcent: (ratio) => pourcentage.format(ratio),
    pourcentSigne: (ratio) => pourcentageSigne.format(ratio),
    eurosSigne: (centimes) => eurosSigne.format(centimes / 100),
    nombreSigne: (valeur) => entierSigne.format(valeur),
    pourcentDePoints: (points) => pourcentage.format(points / 10_000),
    nombre: (valeur) => entier.format(valeur),
    moisCourt: (mois) => moisCourt.format(dateDuMois(mois)),
    moisAnnee: (mois) => moisAnnee.format(dateDuMois(mois)),
    moisLong: (mois) => moisLong.format(dateDuMois(mois)),
    date: (iso) => date.format(new Date(iso)),
    jourMois: (iso) => jourMois.format(new Date(iso)),
    jourMoisAnnee: (iso) => jourMoisAnnee.format(new Date(iso)),
    dateHeure: (iso) => dateHeure.format(new Date(iso)),
  };
}

/** Les formats de la langue courante de l'interface. */
export function useFormats(): Formats {
  const { i18n } = useTranslation('console');
  return creerFormats(langueCourante(i18n.language));
}
